import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { usePlanes } from './usePlanes';
import apiClient from '@/services/apiClient';
import { dolarObservadoService } from '@/services/dolarObservadoService';

// Cache global persistente que sobrevive a navegación
const PAYMENT_CACHE = {
  lastPayment: null,
  dolarObservado: null,
  lastEmpresa: null,
  lastUpdate: null,
  isInitialized: false
};

const CACHE_DURATION = 1800000; // 30 minutos para pagos (cambian menos frecuentemente)

// Flag global para evitar múltiples inicializaciones
let isPaymentInitializing = false;

/**
 * Hook optimizado para obtener información de pagos y precios con conversión a CLP
 */
export const usePaymentHistory = () => {
  // Usar SimpleEmpresa directamente
  const { empresaActual } = useSimpleEmpresa();
  const { planes } = usePlanes();
  const [lastPayment, setLastPayment] = useState(PAYMENT_CACHE.lastPayment);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dolarObservado, setDolarObservado] = useState(PAYMENT_CACHE.dolarObservado);
  
  // Memoizar valor estable para evitar re-renders
  const stableEmpresaId = useMemo(() => empresaActual?.id, [empresaActual?.id]);

  // Función para verificar si necesitamos cargar datos
  const shouldLoadData = useCallback(() => {
    if (!stableEmpresaId) return false;
    
    const now = Date.now();
    const cacheValid = PAYMENT_CACHE.lastEmpresa === stableEmpresaId &&
                      PAYMENT_CACHE.lastUpdate &&
                      (now - PAYMENT_CACHE.lastUpdate) < CACHE_DURATION;
    
    return !cacheValid;
  }, [stableEmpresaId]);

  // Inicialización una sola vez con cache global agresivo
  useEffect(() => {
    // Verificar si ya tenemos todo lo que necesitamos
    if (!stableEmpresaId) {
      console.log('💳 [usePaymentHistory] No empresa, skipping');
      return;
    }

    // Verificar cache válido primero - este es el paso más importante
    const isCacheValid = PAYMENT_CACHE.lastEmpresa === stableEmpresaId &&
                        PAYMENT_CACHE.lastUpdate &&
                        (Date.now() - PAYMENT_CACHE.lastUpdate) < CACHE_DURATION;

    if (isCacheValid && PAYMENT_CACHE.isInitialized) {
      console.log('💳 [usePaymentHistory] Using valid cache, no API call needed');
      if (PAYMENT_CACHE.lastPayment) {
        setLastPayment(PAYMENT_CACHE.lastPayment);
      }
      if (PAYMENT_CACHE.dolarObservado) {
        setDolarObservado(PAYMENT_CACHE.dolarObservado);
      }
      return;
    }

    // Evitar múltiples llamadas simultáneas globalmente
    if (isPaymentInitializing) {
      console.log('💳 [usePaymentHistory] Already initializing globally, skipping');
      return;
    }

    // Solo hacer la llamada si realmente es necesario
    const initializePayment = async () => {
      try {
        console.log('💳 [usePaymentHistory] Initializing payment from API...');
        isPaymentInitializing = true;
        setLoading(true);
        setError(null);
        
        const response = await apiClient.get('/api/payments/last');
        
        if (response.data?.success) {
          PAYMENT_CACHE.lastPayment = response.data.data;
          PAYMENT_CACHE.lastEmpresa = stableEmpresaId;
          PAYMENT_CACHE.lastUpdate = Date.now();
          PAYMENT_CACHE.isInitialized = true;
          setLastPayment(response.data.data);
          console.log('💳 [usePaymentHistory] Payment initialized successfully');
        } else {
          PAYMENT_CACHE.lastPayment = null;
          PAYMENT_CACHE.isInitialized = true;
          setLastPayment(null);
          console.log('💳 [usePaymentHistory] No payment data found');
        }
      } catch (err) {
        console.error('Error initializing payment:', err);
        setError(err.message);
        setLastPayment(null);
      } finally {
        isPaymentInitializing = false;
        setLoading(false);
      }
    };

    initializePayment();
  }, []); // CLAVE: Array vacío para que solo se ejecute una vez

  // Efecto separado que solo actualiza el estado local si el cache cambia para este contexto
  useEffect(() => {
    if (stableEmpresaId && PAYMENT_CACHE.isInitialized) {
      const isCacheForThisContext = PAYMENT_CACHE.lastEmpresa === stableEmpresaId;

      if (isCacheForThisContext && !lastPayment && PAYMENT_CACHE.lastPayment) {
        console.log('💳 [usePaymentHistory] Syncing local state with cache');
        setLastPayment(PAYMENT_CACHE.lastPayment);
      }
    }
  }, [stableEmpresaId, lastPayment]); // Solo actualizar estado local

  // Cargar dólar observado con el nuevo servicio optimizado
  useEffect(() => {
    const loadDolarObservado = async () => {
      // Si ya tenemos el dólar en cache, usarlo
      if (PAYMENT_CACHE.dolarObservado) {
        setDolarObservado(PAYMENT_CACHE.dolarObservado);
        return;
      }

      try {
        const valor = await dolarObservadoService.getDolarObservado();
        PAYMENT_CACHE.dolarObservado = valor;
        setDolarObservado(valor);
      } catch (error) {
        console.error('Error loading dólar observado:', error);
        // Valor fallback redondeado
        const fallbackValue = 950; // Valor más realista como fallback
        PAYMENT_CACHE.dolarObservado = fallbackValue;
        setDolarObservado(fallbackValue);
      }
    };

    loadDolarObservado();
  }, []); // Agregar dependencias vacías para que solo se ejecute una vez 

  // Información procesada del pago con conversiones
  const paymentInfo = useMemo(() => {
    if (!planes.length || !dolarObservado) return null;

    // Si tenemos datos de pago, usarlos. Si no, usar datos del plan de la empresa
    let planId, interval, amount, currency;
    
    if (lastPayment) {
      planId = lastPayment.plan_id;
      interval = lastPayment.plan_interval;
      amount = lastPayment.amount;
      currency = lastPayment.currency;
    } else if (empresaActual?.plan_id) {
      // Fallback: usar datos de la empresa y asumir que es anual
      planId = empresaActual.plan_id;
      interval = 'annual'; // Asumir anual por defecto
      currency = 'CLP';
      
      // Encontrar el plan para obtener el precio
      const currentPlan = planes.find(p => p.id === planId);
      if (currentPlan) {
        // Usar precio anual si existe, si no, calcular desde el mensual
        amount = currentPlan.precio_anual || (currentPlan.price * 12);
      }
    }

    if (!planId || !amount) return null;

    const isMonthly = interval === 'monthly' || interval === 'month';
    const isAnnual = interval === 'yearly' || interval === 'year' || interval === 'annual';
    
    // Encontrar el plan completo desde usePlanes para obtener descuento_anual
    const currentPlan = planes.find(p => p.id === planId);
    
    // Convertir a CLP si el monto está en USD
    let amountCLP = amount;
    if (currency === 'USD') {
      amountCLP = Math.round(amount * dolarObservado);
    }

    // Información base
    const info = {
      planId,
      planName: currentPlan?.name || 'Plan desconocido',
      interval,
      isMonthly,
      isAnnual,
      amount,
      amountCLP,
      currency,
      formattedAmount: `$${amountCLP.toLocaleString('es-CL')}`,
      periodText: isMonthly ? '/mes' : isAnnual ? '/año' : '',
      paymentDate: lastPayment?.payment_date,
      dolarObservado,
      hasValidInterval: isMonthly || isAnnual
    };

    // Si es pago mensual, calcular equivalente anual con descuento
    if (isMonthly && currentPlan) {
      const monthlyPriceUSD = currentPlan.price || amount;
      const annualPriceUSD = currentPlan.price * 12; // Precio anual base sin descuento
      const annualDiscount = currentPlan.descuento_anual || 0; // Obtener descuento desde planes
      
      if (annualDiscount > 0) {
        // Precio anual con descuento aplicado
        const discountedAnnualPriceUSD = annualPriceUSD * (1 - annualDiscount / 100);
        const annualSavingsUSD = annualPriceUSD - discountedAnnualPriceUSD;
        
        info.annualOption = {
          originalPriceUSD: annualPriceUSD,
          discountedPriceUSD: discountedAnnualPriceUSD,
          originalPriceCLP: Math.round(annualPriceUSD * dolarObservado),
          discountedPriceCLP: Math.round(discountedAnnualPriceUSD * dolarObservado),
          savingsUSD: annualSavingsUSD,
          savingsCLP: Math.round(annualSavingsUSD * dolarObservado),
          discountPercentage: annualDiscount,
          formattedOriginalPrice: `$${Math.round(annualPriceUSD * dolarObservado).toLocaleString('es-CL')}`,
          formattedDiscountedPrice: `$${Math.round(discountedAnnualPriceUSD * dolarObservado).toLocaleString('es-CL')}`,
          formattedSavings: `$${Math.round(annualSavingsUSD * dolarObservado).toLocaleString('es-CL')}`
        };
      }
    }

    return info;
  }, [lastPayment, dolarObservado, planes, stableEmpresaId]);

  return {
    lastPayment,
    paymentInfo,
    dolarObservado,
    loading,
    error
  };
};

export default usePaymentHistory;